import React from "react"
import { Table as AntTable, notification, Modal } from "antd"
import { Header } from "src/components/layout/header"
import { VisibilityProvider } from "src/components/visibility-provider"
import { useGetData } from "src/pages-lib/receipts/model"
import { getColumns } from "src/pages-lib/receipts/table-columns"
import { ReceiptDetailDrawer } from "src/pages-lib/receipts/receipt-detail-drawer"
import { ReceiptFilters } from "src/pages-lib/receipts/filters"
import { SummaryCard } from "src/pages-lib/receipts/summary-card"
import { usePermission } from "src/hooks"
import api from "src/api"
import styled from "styled-components"

export default function Receipts() {
  const { hasPermission } = usePermission()
  const canEdit = hasPermission("receipts.edit")
  const [detailOrderId, setDetailOrderId] = React.useState(null)
  const [detailDrawerState, setDetailDrawerState] = React.useState(false)
  const [isExporting, setIsExporting] = React.useState(false)

  const {
    dataItems,
    summary,
    paymentAccounts,
    points,
    pagination,
    isLoading,
    handleTableChange,
    handleSearch,
    getCurrentExportQuery,
    refetch,
  } = useGetData()

  async function onExport() {
    setIsExporting(true)
    try {
      const query = getCurrentExportQuery()
      const file = await api.get(`receipts/export${query ? `?${query}` : ""}`, {
        useV2Api: true,
        responseType: "blob",
      })

      const blob = new Blob([file], { type: "text/csv;charset=utf-8" })
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement("a")
      link.href = url
      link.download = `receipts-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()

      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Ошибка выгрузки CSV", error)
      notification.error({ message: "Ошибка выгрузки CSV" })
    } finally {
      setIsExporting(false)
    }
  }

  function onView(orderId) {
    setDetailOrderId(orderId)
    setDetailDrawerState(true)
  }

  function onCloseDetail() {
    setDetailDrawerState(false)
    setDetailOrderId(null)
  }

  // Причина от бэка/банка (почему чек не сформировался), если она есть.
  function ofdErrorMessage(error, fallback) {
    return error?.response?.data?.message || fallback
  }

  async function sendOfdReceipt(orderId, endpoint, labels) {
    try {
      await api.post(`receipts/${orderId}/${endpoint}`, {}, { useV2Api: true })
      notification.success({ message: labels.success })
    } catch (error) {
      console.error(labels.error, error)
      notification.error({
        message: labels.error,
        description: ofdErrorMessage(error, "Смотрите детали чека и логи запроса/ответа банка."),
        duration: 8,
      })
    } finally {
      // В любом случае открываем детали заказа: там виден свежий чек и полный
      // запрос/ответ банка — чтобы было понятно, что именно произошло.
      refetch()
      onView(orderId)
    }
  }

  function onSendPrepayment(orderId) {
    Modal.confirm({
      title: "Отправить чек предоплаты?",
      content: `Заказ #${orderId}. Чек предоплаты будет создан и подтверждён в ОФД. Действие необратимо.`,
      okText: "Да, отправить",
      okType: "danger",
      cancelText: "Отмена",
      centered: true,
      maskClosable: false,
      onOk: () =>
        sendOfdReceipt(orderId, "ofd-prepayment", {
          success: "Чек предоплаты отправлен",
          error: "Ошибка отправки чека предоплаты",
        }),
    })
  }

  function onSendFullPayment(orderId) {
    Modal.confirm({
      title: "Отправить полный чек?",
      content: `Заказ #${orderId}. Полный чек будет создан и подтверждён в ОФД. Действие необратимо.`,
      okText: "Да, отправить",
      okType: "danger",
      cancelText: "Отмена",
      centered: true,
      maskClosable: false,
      onOk: () =>
        sendOfdReceipt(orderId, "ofd-full-payment", {
          success: "Полный чек отправлен",
          error: "Ошибка отправки полного чека",
        }),
    })
  }

  const columns = getColumns(
    onView,
    canEdit ? onSendPrepayment : null,
    canEdit ? onSendFullPayment : null,
  )

  const dataSource = dataItems.map((item) => ({ ...item, key: item.id }))

  return (
    <>
      <Header title="Чеки по заказам" />

      <ReceiptFilters
        handleSearch={handleSearch}
        onExport={onExport}
        isExporting={isExporting}
        paymentAccounts={paymentAccounts}
        points={points}
      />

      <SummaryCard summary={summary} />

      <StyledTable
        dataSource={dataSource}
        columns={columns}
        loading={isLoading}
        onChange={handleTableChange}
        pagination={pagination}
        scroll={{ x: 1400 }}
        size="small"
      />

      <VisibilityProvider visible={detailDrawerState}>
        <ReceiptDetailDrawer
          orderId={detailOrderId}
          onClose={onCloseDetail}
        />
      </VisibilityProvider>
    </>
  )
}

const StyledTable = styled(AntTable)`
  .ant-table-row {
    cursor: pointer;
  }
`
